import type { PopupDesign } from './popupDefaults';
import { DEMO_GIFTS } from './popupDefaults';

interface PopupPreviewProps {
  design: PopupDesign;
  mode: 'desktop' | 'mobile';
  /** How many gifts appear selected */
  selectedCount?: number;
  gifts?: Array<{ id: string; title: string; meta?: string }>;
}

/** Sleek responsive free-gift pop-up preview (desktop modal / mobile sheet) */
export function PopupPreview({
  design,
  mode,
  selectedCount = 1,
  gifts = DEMO_GIFTS,
}: PopupPreviewProps) {
  const isMobile = mode === 'mobile';
  const list = gifts.slice(0, 3);

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: isMobile ? 20 : 16,
        overflow: 'hidden',
        background: isMobile
          ? 'linear-gradient(180deg, #dfe3e8 0%, #c5ccd3 100%)'
          : design.overlayColor,
        minHeight: isMobile ? 480 : 420,
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 24,
        fontFamily: design.fontFamily,
      }}
    >
      {/* Fake browser chrome (desktop) */}
      {!isMobile && (
        <div
          style={{
            position: 'absolute',
            inset: 12,
            borderRadius: 12,
            background: 'linear-gradient(180deg, #eef0f2, #e2e5e8)',
            opacity: 0.9,
            zIndex: 0,
          }}
        />
      )}

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: isMobile ? '100%' : design.maxWidth,
          background: design.backgroundColor,
          color: design.textColor,
          borderRadius: isMobile
            ? `${design.borderRadius}px ${design.borderRadius}px 0 0`
            : design.borderRadius,
          boxShadow: '0 20px 50px rgba(0,0,0,.18)',
          padding: isMobile ? '20px 18px 24px' : '22px 22px 20px',
          marginTop: isMobile ? 40 : 0,
        }}
      >
        {isMobile && (
          <div
            style={{
              width: 40,
              height: 4,
              borderRadius: 99,
              background: '#D0D4D8',
              margin: '0 auto 14px',
            }}
          />
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
            marginBottom: 8,
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: isMobile ? 17 : 18,
                fontWeight: 700,
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
              }}
            >
              {design.title}
            </div>
            <div
              style={{
                marginTop: 6,
                fontSize: 13,
                lineHeight: 1.45,
                color: design.mutedColor,
              }}
            >
              {design.description}
            </div>
          </div>
          {design.showClose && (
            <button
              type="button"
              aria-label="Close"
              style={{
                border: 'none',
                background: 'transparent',
                color: design.mutedColor,
                fontSize: 18,
                cursor: 'default',
                lineHeight: 1,
                padding: 4,
              }}
            >
              ×
            </button>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
          {list.map((g, idx) => {
            const selected = idx < selectedCount;
            return (
              <div
                key={g.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 12,
                  background: design.giftCardBg,
                  border: `1.5px solid ${selected ? design.selectedBorder : design.giftCardBorder}`,
                  transition: 'border-color .15s ease',
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 10,
                    background: '#fff',
                    border: `1px solid ${design.giftCardBorder}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    color: design.accentColor,
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{g.title}</div>
                  <div style={{ fontSize: 12, color: design.mutedColor, marginTop: 2 }}>
                    {g.meta || 'Free gift'}
                  </div>
                </div>
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    border: selected
                      ? `none`
                      : `1.5px solid ${design.giftCardBorder}`,
                    background: selected ? design.accentColor : '#fff',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {selected ? '✓' : ''}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          style={{
            marginTop: 16,
            width: '100%',
            border: 'none',
            borderRadius: 10,
            padding: '13px 16px',
            background: design.accentColor,
            color: design.buttonTextColor,
            fontWeight: 650,
            fontSize: 14,
            cursor: 'default',
            letterSpacing: '0.01em',
          }}
        >
          {design.ctaLabel}
          {selectedCount > 0 ? ` (${selectedCount})` : ''}
        </button>

        {design.secondaryLabel && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 12,
              fontSize: 13,
              color: design.mutedColor,
              fontWeight: 500,
            }}
          >
            {design.secondaryLabel}
          </div>
        )}
      </div>
    </div>
  );
}
